import { generateId, safeGetJson, saveWithLimit } from './safe-storage';

export type EventType = 'invoice-due' | 'meeting' | 'tax-deadline' | 'reminder' | 'workflow' | 'milestone';

export interface CalendarEvent {
  id: string;
  title: string;
  type: EventType;
  date: string;
  description?: string;
  link?: string;
  acknowledged: boolean;
}

const STORAGE_KEY = 'kontora_calendar_events_v1';
const MAX_EVENTS = 100;

export function loadEvents(): CalendarEvent[] {
  return safeGetJson<CalendarEvent[]>(STORAGE_KEY, []);
}

export function saveEvents(events: CalendarEvent[]): void {
  saveWithLimit(STORAGE_KEY, events, MAX_EVENTS);
}

export function createEvent(opts: Omit<CalendarEvent, 'id' | 'acknowledged'>): CalendarEvent {
  const event: CalendarEvent = {
    ...opts,
    id: generateId('evt'),
    acknowledged: false,
  };
  const all = loadEvents();
  all.push(event);
  saveEvents(all);
  return event;
}

export function deleteEvent(id: string): boolean {
  const all = loadEvents();
  const next = all.filter((e) => e.id !== id);
  if (next.length === all.length) return false;
  saveEvents(next);
  return true;
}

export function acknowledgeEvent(id: string): boolean {
  const all = loadEvents();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return false;
  const existing = all[idx];
  if (!existing) return false;
  all[idx] = { ...existing, acknowledged: true };
  saveEvents(all);
  return true;
}

export function getEventsForDate(date: Date, events?: CalendarEvent[]): CalendarEvent[] {
  const all = events ?? loadEvents();
  const key = date.toISOString().slice(0, 10);
  return all.filter((e) => e.date.startsWith(key));
}

export function getUpcomingEvents(days: number, events?: CalendarEvent[]): CalendarEvent[] {
  const all = events ?? loadEvents();
  const now = Date.now();
  const future = now + days * 24 * 60 * 60 * 1000;
  return all
    .filter((e) => {
      const t = new Date(e.date).getTime();
      return t >= now - 24 * 60 * 60 * 1000 && t <= future;
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

export function getEventsForMonth(year: number, month: number, events?: CalendarEvent[]): CalendarEvent[] {
  const all = events ?? loadEvents();
  return all.filter((e) => {
    const d = new Date(e.date);
    return d.getFullYear() === year && d.getMonth() === month;
  });
}

export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export function typeLabel(type: EventType): string {
  const labels: Record<EventType, string> = {
    'invoice-due': 'Rechnungs-Fälligkeit',
    meeting: 'Meeting',
    'tax-deadline': 'Steuer-Frist',
    reminder: 'Erinnerung',
    workflow: 'Workflow',
    milestone: 'Meilenstein',
  };
  return labels[type];
}

export function typeColorVar(type: EventType): string {
  const map: Record<EventType, string> = {
    'invoice-due': 'var(--k-color-warning-strong)',
    meeting: 'var(--k-color-accent)',
    'tax-deadline': 'var(--k-color-danger-strong)',
    reminder: 'var(--k-color-text-secondary)',
    workflow: 'var(--k-color-success-strong)',
    milestone: 'var(--k-color-accent-strong)',
  };
  return map[type];
}

export function typeEmoji(type: EventType): string {
  const map: Record<EventType, string> = {
    'invoice-due': '💳',
    meeting: '👥',
    'tax-deadline': '⚠️',
    reminder: '🔔',
    workflow: '⚙️',
    milestone: '🏆',
  };
  return map[type];
}

export function getRelativeDay(date: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Heute';
  if (diffDays === 1) return 'Morgen';
  if (diffDays === -1) return 'Gestern';
  if (diffDays > 0 && diffDays < 7) return `In ${diffDays} Tagen`;
  if (diffDays < 0 && diffDays > -7) return `Vor ${-diffDays} Tagen`;
  return target.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
}

export function seedDemoEvents(): CalendarEvent[] {
  const today = new Date();
  const events: Omit<CalendarEvent, 'id' | 'acknowledged'>[] = [
    { title: 'UStVA abgeben', type: 'tax-deadline', date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 5).toISOString() },
    { title: 'Rechnung RE-2025-001 fällig', type: 'invoice-due', date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 2).toISOString() },
    { title: 'Team-Meeting', type: 'meeting', date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1, 14, 0).toISOString() },
    { title: 'Monatsabschluss Q1', type: 'milestone', date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 12).toISOString() },
    { title: 'Workflow: Bankimport', type: 'workflow', date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3).toISOString() },
    { title: 'Erinnerung Steuerberater', type: 'reminder', date: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7).toISOString() },
    { title: 'Rechnung RE-2025-002 überfällig', type: 'invoice-due', date: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 5).toISOString() },
  ];
  const existing = loadEvents();
  if (existing.length > 0) return existing;
  const created = events.map(createEvent);
  return created;
}
